import * as React from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface UserProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  preferred_locale: string | null;
  preferred_market: string | null;
  phone_e164: string | null;
  self_country: string | null;
  self_city: string | null;
  self_address1: string | null;
  self_driver_notes: string | null;
  gender_self: "male" | "female" | null;
  nationality: string | null; // ISO-2
  birth_date: string | null; // YYYY-MM-DD
  created_at: string;
}

export interface ProfilePatch {
  display_name?: string | null;
  phone_e164?: string | null;
  preferred_market?: string | null;
  self_country?: string | null;
  self_city?: string | null;
  self_address1?: string | null;
  self_driver_notes?: string | null;
  gender_self?: "male" | "female" | null;
  nationality?: string | null;
  birth_date?: string | null;
}

interface UpdateProfileOptions {
  /** @deprecated الـRPC `is_phone_available` تستثني صفّ المستخدم تلقائياً منذ 2026-06-05 — الخيار غير ضروريّ. */
  ignoreSamePhoneAvailability?: boolean;
}

/**
 * يقرأ/يحدّث الحقول الموسّعة في profiles (الهاتف + العنوان الشخصي
 * المستخدم في «هديّة لنفسي»).
 */
export function useUserProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!user) {
      setProfile(null);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select(
          "id, display_name, avatar_url, preferred_locale, preferred_market, phone_e164, self_country, self_city, self_address1, self_driver_notes, gender_self, nationality, birth_date, created_at",
        )
        .eq("id", user.id)
        .maybeSingle();
      if (error) throw error;
      setProfile((data as UserProfile | null) ?? null);
    } catch (err) {
      console.error("useUserProfile refresh failed:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const update = React.useCallback(
    async (patch: ProfilePatch, _options?: UpdateProfileOptions) => {
      if (!user) return;
      // فحص توفّر الهاتف فقط إذا تغيّر فعلاً عن القيمة الحاليّة للمستخدم.
      if (typeof patch.phone_e164 !== "undefined") {
        const nextPhone = patch.phone_e164?.trim() || null;
        const currentPhone = profile?.phone_e164 ?? null;
        if (nextPhone && nextPhone !== currentPhone) {
          const { data: avail, error: availError } = await supabase.rpc("is_phone_available", { _phone: nextPhone });
          if (!availError && avail === false) {
            throw new Error("PHONE_ALREADY_REGISTERED");
          }
        }
      }
      // أرسل فقط الحقول الموجودة في الـpatch.
      if (Object.keys(patch).length === 0) return;
      // نستخدم .select() لنُجبر PostgREST على إرجاع الصفوف المحدَّثة،
      // وبذلك يتحوّل أيّ فشل صامت (RLS يفلتر الصفّ أو trigger يرجع NULL)
      // إلى خطأ صريح بدل توهّم نجاح الحفظ.
      const { data: updated, error } = await supabase
        .from("profiles")
        .update(patch)
        .eq("id", user.id)
        .select("id")
        .maybeSingle();
      if (error) {
        console.error("useUserProfile update failed:", error, "patch:", patch);
        throw error;
      }
      if (!updated) {
        console.error("useUserProfile update returned 0 rows. patch:", patch, "user:", user.id);
        throw new Error("PROFILE_UPDATE_NO_ROW");
      }
      await refresh();
    },
    [user, profile, refresh],
  );

  return { profile, loading, refresh, update };
}
